/**
 * Bundle builder — quantity mode (add flavor products to cart) or legacy pack-size mode.
 */
function initSimplePack() {
  const form = document.getElementById('simple-pack-form');
  if (!form) return;

  const root = form.closest('.simple-pack');
  const mode = root?.dataset.builderMode || 'quantity';
  const config = root?.querySelector('[data-pack-messages]') || form;
  const sizeSelect = document.getElementById('simple-pack-size');
  const countEl = form.querySelector('[data-pack-count]');
  const currentEl = form.querySelector('[data-pack-count-current]');
  const limitEl = form.querySelector('[data-pack-count-limit]');
  const messageEl = form.querySelector('[data-pack-message]');
  const estimateEl = form.querySelector('[data-pack-estimate]');
  const submitBtn = form.querySelector('[data-pack-submit]');

  const getInputs = () => [...form.querySelectorAll('.simple-pack__flavor-input')];

  const getPackLimit = () => {
    if (mode !== 'pack_size') return 0;
    if (sizeSelect) {
      const option = sizeSelect.options[sizeSelect.selectedIndex];
      return parseInt(option?.dataset.packQty, 10) || 0;
    }
    return parseInt(config.dataset.packSize, 10) || 0;
  };

  const getTotal = () =>
    getInputs().reduce((sum, input) => sum + (parseInt(input.value, 10) || 0), 0);

  const getBulkEligibleTotal = () =>
    getInputs()
      .filter((input) => input.dataset.bulkEligible === 'true')
      .reduce((sum, input) => sum + (parseInt(input.value, 10) || 0), 0);

  const formatMessage = (template, values) => {
    if (!template) return '';
    return Object.entries(values).reduce(
      (message, [key, value]) => message.replaceAll(`__${key}__`, String(value)),
      template
    );
  };

  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    return `$${amount.replace(/\.00$/, '')}`;
  };

  const getMinOrderCents = () => {
    if (config.dataset.enableMinOrder !== 'true') return 0;
    return parseInt(config.dataset.minOrderCents, 10) || 0;
  };

  const getEstimateCents = () => {
    const bulkMin = parseInt(config.dataset.bulkMin, 10) || 0;
    const bulkMax = parseInt(config.dataset.bulkMax, 10) || 0;
    const bulkEnabled = config.dataset.enableBulk !== 'false';
    const bulkEligibleQty = getBulkEligibleTotal();
    const applyBulk =
      bulkEnabled && bulkMin && bulkMax && bulkEligibleQty >= bulkMin && bulkEligibleQty <= bulkMax;

    return getInputs().reduce((sum, input) => {
      const qty = parseInt(input.value, 10) || 0;
      if (!qty) return sum;

      let unit = parseInt(input.dataset.unitPrice, 10) || 0;
      if (applyBulk && input.dataset.bulkEligible === 'true') {
        unit = parseInt(input.dataset.bulkUnitPrice, 10) || unit;
      }

      return sum + qty * unit;
    }, 0);
  };

  const setMessage = (text, isError = false) => {
    if (!messageEl) return;
    messageEl.textContent = text;
    messageEl.hidden = !text;
    messageEl.classList.toggle('simple-pack__message--error', isError);
  };

  const updateEstimate = (total) => {
    if (!estimateEl || mode !== 'quantity') return;
    if (total <= 0 || getEstimateCents() <= 0) {
      estimateEl.hidden = true;
      estimateEl.textContent = '';
      return;
    }

    estimateEl.hidden = false;
    estimateEl.textContent = formatMessage(config.dataset.msgEstimate || 'Estimated total: __TOTAL__', {
      TOTAL: formatMoney(getEstimateCents()),
      QTY: total,
    });
  };

  const updateCount = () => {
    const limit = getPackLimit();
    const total = getTotal();

    if (currentEl) currentEl.textContent = String(total);
    if (limitEl) limitEl.textContent = String(limit);

    if (countEl) {
      countEl.hidden = mode !== 'pack_size' || !limit;
      countEl.classList.toggle('is-complete', limit > 0 && total === limit);
      countEl.classList.toggle('is-over', limit > 0 && total > limit);
    }

    updateEstimate(total);

    if (mode === 'quantity') {
      if (total <= 0) {
        setMessage(config.dataset.msgEmpty || 'Choose at least one can to continue.');
        if (submitBtn) submitBtn.disabled = true;
        return;
      }

      const minOrderCents = getMinOrderCents();
      const estimateCents = getEstimateCents();
      if (minOrderCents > 0 && estimateCents < minOrderCents) {
        setMessage(
          formatMessage(config.dataset.msgMinOrder || 'Add __REMAINING__ more to reach the __MINIMUM__ minimum.', {
            REMAINING: formatMoney(minOrderCents - estimateCents),
            MINIMUM: formatMoney(minOrderCents),
          }),
          true
        );
        if (submitBtn) submitBtn.disabled = true;
        return;
      }

      setMessage('');
      if (submitBtn) submitBtn.disabled = false;
      return;
    }

    if (!limit) {
      setMessage('');
      if (submitBtn) submitBtn.disabled = !getInputs().length;
      return;
    }

    const remaining = limit - total;

    if (total > limit) {
      setMessage(
        formatMessage(config.dataset.msgOver, {
          TOTAL: limit,
          EXCESS: total - limit,
        }),
        true
      );
    } else if (total < limit) {
      setMessage(
        formatMessage(config.dataset.msgUnder, {
          REMAINING: remaining,
          TOTAL: limit,
        })
      );
    } else {
      setMessage(config.dataset.msgComplete || 'Perfect — your pack is full.');
    }

    if (submitBtn) submitBtn.disabled = total !== limit || !getInputs().length;
  };

  const clampInputs = () => {
    const limit = getPackLimit();
    if (!limit) return;

    let total = getTotal();
    if (total <= limit) return;

    let excess = total - limit;
    [...getInputs()].reverse().forEach((input) => {
      const value = parseInt(input.value, 10) || 0;
      if (excess <= 0 || value <= 0) return;
      const reduce = Math.min(value, excess);
      input.value = String(value - reduce);
      excess -= reduce;
    });
  };

  const onFlavorInput = (event) => {
    if (!event.target.matches('.simple-pack__flavor-input')) return;

    const limit = getPackLimit();
    const input = event.target;
    let value = parseInt(input.value, 10) || 0;

    if (value < 0) value = 0;

    if (limit) {
      const others = getTotal() - (parseInt(input.value, 10) || 0);
      const maxForInput = Math.max(0, limit - others);
      if (value > maxForInput) value = maxForInput;
    }

    input.value = String(value);
    updateCount();
  };

  const onPackSizeChange = () => {
    clampInputs();
    updateCount();
  };

  const buildQuantityItems = () =>
    getInputs()
      .map((input) => ({
        id: parseInt(input.dataset.variantId, 10),
        quantity: parseInt(input.value, 10) || 0,
      }))
      .filter((item) => item.id && item.quantity > 0);

  const getBulkThresholds = () => ({
    min: parseInt(config.dataset.bulkMin, 10) || 0,
    max: parseInt(config.dataset.bulkMax, 10) || 0,
    code: (config.dataset.bulkDiscountCode || '').trim(),
  });

  const qualifiesForBulk = () => {
    if (config.dataset.enableBulk === 'false') return false;
    const { min, max } = getBulkThresholds();
    const bulkEligibleQty = getBulkEligibleTotal();
    return min > 0 && max > 0 && bulkEligibleQty >= min && bulkEligibleQty <= max;
  };

  const applyBulkDiscount = async (code) => {
    if (!code) return false;

    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}discount/${encodeURIComponent(code)}`, {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'manual',
    });

    return res.ok || res.status === 302 || res.type === 'opaqueredirect';
  };

  const addItemsToCart = async (items) => {
    const payload = items
      .map((item) => ({
        id: parseInt(item.id, 10),
        quantity: parseInt(item.quantity, 10) || 0,
      }))
      .filter((item) => item.id && item.quantity > 0);

    if (!payload.length) {
      throw new Error(config.dataset.msgEmpty || 'Choose at least one can to continue.');
    }

    if (window.CakeCart?.addItems) {
      await window.CakeCart.addItems(payload);
    } else {
      const res = await fetch(
        window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: payload }),
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add to cart');
      }
    }

    let bulkDiscountWarning = '';

    if (qualifiesForBulk()) {
      const { code } = getBulkThresholds();

      if (code) {
        const applied = await applyBulkDiscount(code);
        if (!applied) {
          bulkDiscountWarning = formatMessage(config.dataset.msgBulkMissing || '', { CODE: code });
        }
      } else {
        bulkDiscountWarning = config.dataset.msgBulkMissing || '';
      }
    }

    if (window.CakeCart?.afterAdd) {
      await window.CakeCart.afterAdd();
    } else {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        await drawer.refresh();
        drawer.open();
        await drawer.updateCartCount();
      } else {
        window.location.href = '/cart';
      }
    }

    if (bulkDiscountWarning) setMessage(bulkDiscountWarning, true);
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    const limit = getPackLimit();
    const total = getTotal();

    if (mode === 'quantity') {
      const items = buildQuantityItems();
      if (!items.length) {
        updateCount();
        return;
      }

      const minOrderCents = getMinOrderCents();
      const estimateCents = getEstimateCents();
      if (minOrderCents > 0 && estimateCents < minOrderCents) {
        updateCount();
        return;
      }

      if (submitBtn) submitBtn.disabled = true;
      try {
        await addItemsToCart(items);
        getInputs().forEach((input) => {
          input.value = '0';
        });
        updateCount();
      } catch (err) {
        setMessage(err.message, true);
      } finally {
        updateCount();
      }
      return;
    }

    if (limit && total !== limit) {
      updateCount();
      countEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    getInputs().forEach((input) => {
      if ((parseInt(input.value, 10) || 0) === 0) input.removeAttribute('name');
    });

    form.submit();
  };

  form.addEventListener('input', onFlavorInput);
  form.addEventListener('change', onFlavorInput);
  form.addEventListener('submit', onSubmit);
  sizeSelect?.addEventListener('change', onPackSizeChange);

  updateCount();
}

document.addEventListener('DOMContentLoaded', initSimplePack);
